import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useQuickFillRevitSync } from '../../../shared/hooks/useQuickFillRevitSync';
import { validateField } from '../../../shared/lib/utils/validationUtils';
import type { QuickFillItem, QuickFillProperty } from '../../../shared/types';
import type {
  PropertyMapping,
} from '../../../shared/services/quickFillRevitService';
import {
  resolveQuickFillPropertyBindingScope,
  type QuickFillConfiguredBindingKind,
} from '../lib/quickFillPageModel';
import type {
  QuickFillTableCellStageResult,
  QuickFillTableCellStageUpdate,
} from '../lib/quickFillTableOperationHelpers';

const EMPTY_BINDING_KINDS: ReadonlyMap<string, QuickFillConfiguredBindingKind> = new Map();

export function useQuickFillMutationOperations(
  parameterBindingKinds: ReadonlyMap<string, QuickFillConfiguredBindingKind> = EMPTY_BINDING_KINDS
) {
  const properties = useQuickFillStore((state) => state.properties);
  const quickFillData = useQuickFillStore((state) => state.quickFillData);
  const editedCells = useQuickFillStore((state) => state.editedCells);
  const setQuickFillData = useQuickFillStore((state) => state.setQuickFillData);
  const commitQuickFillDataAsBaseline = useQuickFillStore(
    (state) => state.commitQuickFillDataAsBaseline
  );
  const captureSessionUndoSnapshot = useQuickFillStore(
    (state) => state.captureSessionUndoSnapshot
  );
  const clearSessionUndoSnapshots = useQuickFillStore(
    (state) => state.clearSessionUndoSnapshots
  );
  const autoFillRowsWithLabels = useQuickFillStore((state) => state.autoFillRowsWithLabels);
  const snackbar = useSnackbarStore();
  const revitSync = useQuickFillRevitSync();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const snapshotSignatureRef = useRef('');
  const sessionUndoSessionIdRef = useRef(
    `quickfill-page-visit-${Date.now()}-${Math.round(Math.random() * 1000)}`
  );

  const getPropertyBindingScope = useCallback(
    (property: Pick<QuickFillProperty, 'revitParameter' | 'isInstance'> | null | undefined) =>
      property ? resolveQuickFillPropertyBindingScope(property, parameterBindingKinds) : undefined,
    [parameterBindingKinds]
  );

  const buildPropertyMapping = useCallback(
    (property: QuickFillProperty & { revitParameter: string }): PropertyMapping => {
      const bindingScope = getPropertyBindingScope(property);
      return {
        label: property.label,
        revitParameter: property.revitParameter,
        ...(bindingScope ? { bindingScope } : {}),
      };
    },
    [getPropertyBindingScope]
  );

  const canEditCell = useCallback((item: QuickFillItem | undefined, field: string) => {
    if (!item || field === 'Name') return false;
    const flags = ((item as any).__readonlyFlags as Record<string, boolean> | undefined) || {};
    return flags[field] !== true;
  }, []);

  const getBindingScope = useCallback((item: QuickFillItem | undefined, field: string) => {
    const flags = (((item as any)?.__bindingScopeFlags as Record<string, string> | undefined) || {});
    return flags[field] ?? 'unknown';
  }, []);

  const getFamilyTypeKey = useCallback((item: QuickFillItem | undefined) => {
    const rawValue = (item as any)?.__familyTypeKey;
    return typeof rawValue === 'string' && rawValue.trim() !== ''
      ? rawValue.trim()
      : item?.Name || '';
  }, []);

  const getRelatedRowIndicesForField = useCallback((
    data: QuickFillItem[], rowIndex: number, field: string
  ): number[] => {
    const row = data[rowIndex];
    if ((row as any)?.__rowMode !== 'instance' || getBindingScope(row, field) !== 'type') {
      return [rowIndex];
    }
    const familyTypeKey = getFamilyTypeKey(row);
    if (!familyTypeKey) return [rowIndex];
    return data.reduce<number[]>((indices, candidate, candidateIndex) => {
      if (
        getFamilyTypeKey(candidate) === familyTypeKey &&
        getBindingScope(candidate, field) === 'type'
      ) indices.push(candidateIndex);
      return indices;
    }, []);
  }, [getBindingScope, getFamilyTypeKey]);

  const applyFieldValueToRows = useCallback((
    data: QuickFillItem[], rowIndices: number[], field: keyof QuickFillItem, value: string
  ) => {
    const updatedData = [...data];
    const touchedRowIndices = new Set<number>();
    rowIndices.forEach((rowIndex) => {
      getRelatedRowIndicesForField(updatedData, rowIndex, String(field)).forEach((targetIndex) => {
        const currentRow = updatedData[targetIndex];
        if (!currentRow || !canEditCell(currentRow, String(field))) return;
        if (String(currentRow[field] ?? '') === value) return;
        updatedData[targetIndex] = { ...currentRow, [field]: value };
        touchedRowIndices.add(targetIndex);
      });
    });
    return {
      updatedData,
      touchedRowIndices: Array.from(touchedRowIndices).sort((left, right) => left - right),
    };
  }, [canEditCell, getRelatedRowIndicesForField]);

  const captureSessionUndoBeforeMutation = useCallback((label: string) => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const sessionId = sessionUndoSessionIdRef.current;
    if (data.length === 0) return { snapshotId: null, wasCaptured: false };
    const alreadyCaptured = (useQuickFillStore.getState().sessionUndoSnapshots || [])
      .some((snapshot) => snapshot.sessionId === sessionId);
    const snapshotId = captureSessionUndoSnapshot(label, sessionId);
    return { snapshotId, wasCaptured: Boolean(snapshotId && !alreadyCaptured) };
  }, [captureSessionUndoSnapshot, quickFillData]);

  const releaseEmptySessionUndoCapture = useCallback(
    (capture: { snapshotId: string | null; wasCaptured: boolean }) => {
      if (capture.wasCaptured && capture.snapshotId) clearSessionUndoSnapshots([capture.snapshotId]);
    },
    [clearSessionUndoSnapshots]
  );

  useEffect(() => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    // Structural signature over identity fields only; JSON.stringify of full
    // rows here serialized the entire dataset on every cell edit.
    let signature = String(data.length);
    for (const item of data) {
      signature += `\n${item?.Name || ''}${(item as any)?.__rowMode || 'familyType'}${(item as any)?.__elementId || ''}`;
    }
    if (signature !== snapshotSignatureRef.current && data.length > 0) {
      revitSync.setOriginalData(quickFillData);
      snapshotSignatureRef.current = signature;
    }
  }, [quickFillData, revitSync]);

  const handleTableDataChange = useCallback(async (
    index: number, field: keyof QuickFillItem, value: string
  ) => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    if (!canEditCell(data[index], String(field))) return;
    const applied = applyFieldValueToRows(data, [index], field, value);
    if (applied.touchedRowIndices.length === 0) return;
    captureSessionUndoBeforeMutation('Cell edit');
    setQuickFillData(applied.updatedData);
  }, [applyFieldValueToRows, canEditCell, captureSessionUndoBeforeMutation, quickFillData, setQuickFillData]);

  const stageCellUpdates = useCallback((
    updates: QuickFillTableCellStageUpdate[], label = 'Cell edits'
  ): QuickFillTableCellStageResult => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    if (updates.length === 0 || data.length === 0) {
      return { updatedCells: 0, touchedRowIndices: [] };
    }
    let updatedData = [...data];
    const touched = new Set<number>();
    let updatedCells = 0;
    updates.forEach((update) => {
      if (!canEditCell(updatedData[update.rowIndex], String(update.field))) return;
      const applied = applyFieldValueToRows(
        updatedData, [update.rowIndex], update.field, update.value
      );
      updatedData = applied.updatedData;
      updatedCells += applied.touchedRowIndices.length;
      applied.touchedRowIndices.forEach((rowIndex) => touched.add(rowIndex));
    });
    if (updatedCells === 0) return { updatedCells: 0, touchedRowIndices: [] };
    captureSessionUndoBeforeMutation(label);
    setQuickFillData(updatedData);
    return {
      updatedCells,
      touchedRowIndices: Array.from(touched).sort((left, right) => left - right),
    };
  }, [applyFieldValueToRows, canEditCell, captureSessionUndoBeforeMutation, quickFillData, setQuickFillData]);

  const handleSelectRow = useCallback((index: number) => {
    setSelectedRows((current) => current.includes(index)
      ? current.filter((item) => item !== index)
      : [...current, index]);
  }, []);

  const handleSelectAllRows = useCallback((rowIndices?: number[]) => {
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const targetRows = rowIndices?.length ? rowIndices : data.map((_item, index) => index);
    if (targetRows.length === 0) return;
    setSelectedRows((current) => {
      if (targetRows.every((index) => current.includes(index))) {
        return current.filter((index) => !targetRows.includes(index));
      }
      return Array.from(new Set([...current, ...targetRows])).sort((left, right) => left - right);
    });
  }, [quickFillData]);

  const replaceSelectedRows = useCallback((rowIndices: number[]) => {
    setSelectedRows(Array.from(new Set((rowIndices || []).filter(
      (index) => Number.isInteger(index) && index >= 0
    ))).sort((left, right) => left - right));
  }, []);

  const resolveTargetRowIndices = useCallback((rowIndices?: number[]) => {
    const sourceRows = rowIndices?.length ? rowIndices : selectedRows;
    return Array.from(new Set(sourceRows.filter(
      (index) => Number.isInteger(index) && index >= 0
    ))).sort((left, right) => left - right);
  }, [selectedRows]);

  const handleBulkEdit = useCallback(async (
    field: keyof QuickFillItem, value: string, rowIndices?: number[]
  ) => {
    const targetRows = resolveTargetRowIndices(rowIndices);
    if (!field || !value || targetRows.length === 0) return;
    const validation = validateField(field, value);
    if (!validation.isValid) {
      snackbar.showError(validation.error!);
      return;
    }
    const data = Array.isArray(quickFillData) ? quickFillData : [];
    const editableRows = targetRows.filter((index) => canEditCell(data[index], String(field)));
    if (editableRows.length === 0) {
      snackbar.showWarning('No editable cells selected for this column');
      return;
    }
    const applied = applyFieldValueToRows(data, editableRows, field, value);
    if (applied.touchedRowIndices.length === 0) {
      snackbar.showWarning('No selected editable cells changed');
      return;
    }
    captureSessionUndoBeforeMutation('Bulk set');
    setQuickFillData(applied.updatedData);
    // Staged only: the Revit document is written by the Apply/Continue submit path.
    snackbar.showSuccess(`Applied "${value}" to ${applied.touchedRowIndices.length} rows`);
  }, [
    applyFieldValueToRows, canEditCell, captureSessionUndoBeforeMutation,
    quickFillData, resolveTargetRowIndices, setQuickFillData, snackbar,
  ]);

  return {
    properties,
    quickFillData,
    editedCells,
    setQuickFillData,
    commitQuickFillDataAsBaseline,
    autoFillRowsWithLabels,
    snackbar,
    revitSync,
    selectedRows,
    getPropertyBindingScope,
    buildPropertyMapping,
    canEditCell,
    applyFieldValueToRows,
    captureSessionUndoBeforeMutation,
    releaseEmptySessionUndoCapture,
    handleTableDataChange,
    stageCellUpdates,
    handleSelectRow,
    handleSelectAllRows,
    replaceSelectedRows,
    handleBulkEdit,
  };
}

export type QuickFillMutationOperations = ReturnType<typeof useQuickFillMutationOperations>;
